/**
 * Context for managing client-side state specific to the Idea AI page
 * Separates UI state from server state managed by React Query
 */

import React, {createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActiveFilter } from '../../hooks/useFilters';
import { parseFiltersFromQuery, buildFilterUrlParams } from '../ideas/ideasHelpers';

import {useIdeaFilters} from "../../hooks/useIdeaFilters";
import { buildFiltersConfigStructureSignature } from '../../hooks/ideaPage/filterHelpers';
import IdeaPagePerspectiveFetcher from './IdeaPagePerspectiveFetcher';
import {evidenceInclusionToState, stateToEvidenceInclusion} from "../../hooks/useEvidenceInclusion";
import { EvidenceInclusion } from '../ideas/store/types';

interface IdeaPageState {
  // UI State
  activeTab: string | null;
  isGenerated: boolean;
  isValidationDone: boolean;
  selectedEvidenceIds: any[];
  setSelectedEvidenceIds: React.Dispatch<React.SetStateAction<any[]>>;
  chosenEvidence: any | null;
  addEvidenceFormScreen: boolean;
  activityOpened: boolean;
  isSearching: boolean;
  isThemeFound: boolean;
  ideaHistoryView: boolean;
  setIdeaHistoryView: React.Dispatch<React.SetStateAction<boolean>>;
  // Search & Filter State  
  searchQuery: string;
  debouncedSearchQuery: string;
  sortConfig: {
    field?: string;
    direction?: 'asc' | 'desc';
  };
  activeFilters: ActiveFilter[];
  trendFilters: ActiveFilter[];  // Separate state for date/time filters (week, quarter, month)
  evidenceInclusion: EvidenceInclusion;
  suggested: boolean;
  aiAgentPanelVariant: 'default' | 'validate';
  validateHelperFullyAddressedIds: string[] | null;
  // Theme State
  selectedTheme: any | null;
  themeEvidenceIds: any[] | null;
  filteredThemeEvidenceIds: any[] | null;
  setFilteredThemeEvidenceIds: (ids: any[]) => void;
  // Modal State
  verifyOpened: boolean;
  validateLoader: boolean;
  opened: boolean;
  
  // Perspective State
  perspectiveData: { [key: string]: any };
  perspectiveDataLoading: boolean;
  chosenKey: string;
  
  // Graph Selection State
  graphSelection: string;
  
  // Theme management
  themeLoading: boolean;
  setThemeLoading: (loading: boolean) => void;
  recalculateThemes: boolean;
  setRecalculateThemes: (recalculate: boolean) => void;
  setIsThemeFound: (found: boolean) => void;
}

interface IdeaPageActions {
  // UI Actions
  setActiveTab: (tab: string | null) => void;
  setSelectedEvidenceIds: (ids: any[]) => void;
  setChosenEvidence: (evidence: any | null) => void;
  setAddEvidenceFormScreen: (open: boolean) => void;
  setActivityOpened: (open: boolean) => void;
  setIsSearching: (searching: boolean) => void;
  
  // Search & Filter Actions
  setSearchQuery: (query: string) => void;
  setDebouncedSearchQuery: (query: string) => void;
  setSortConfig: (config: { field?: string; direction?: 'asc' | 'desc' }) => void;
  setActiveFilters: (filters: ActiveFilter[]) => void;
  setTrendFilters: (filters: ActiveFilter[]) => void;
  setEvidenceInclusion: (inclusion: EvidenceInclusion) => void;
  setSuggested: (checked: boolean) => void;
  setAiAgentPanelVariant: (v: 'default' | 'validate') => void;
  setValidateHelperFullyAddressedIds: (ids: string[] | null) => void;
  clearSearch: () => void;
  clearSort: () => void;
  
  // Theme Actions
  setSelectedTheme: (theme: any | null) => void;
  setThemeEvidenceIds: (ids: any[] | null) => void;
  
  // Modal Actions
  setVerifyOpened: (open: boolean) => void;
  setValidateLoader: (loading: boolean) => void;
  setOpened: (open: boolean) => void;
  
  // Perspective Actions
  setPerspectiveData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  setPerspectiveDataLoading: (loading: boolean) => void;
  setChosenKey: (key: string) => void;
  
  // Graph Selection Actions
  setGraphSelection: (selection: string) => void;
  unreadNotificationsMap: any[]; 
  setUnreadNotificationsMap: React.Dispatch<React.SetStateAction<any[]>>;
  setIsGenerated: (generated: boolean) => void;
  setIsValidationDone: (validationDone: boolean) => void;
}

type IdeaPageContextType = IdeaPageState & IdeaPageActions;

const initialState: IdeaPageState = {
  // UI State
  activeTab: null,
  isGenerated: false,
  isValidationDone: false,
  selectedEvidenceIds: [],
  setSelectedEvidenceIds: () => {
    console.log('setSelectedEvidenceIds called with default implementation');
  },
  chosenEvidence: null,
  addEvidenceFormScreen: false,
  activityOpened: false,
  isSearching: false,
  isThemeFound: false,
  ideaHistoryView: false,
  setIdeaHistoryView: () => {
    console.log('setIdeaHistoryView called with default implementation');
  },
  // Search & Filter State
  searchQuery: '',
  debouncedSearchQuery: '',
  sortConfig: { field: 'createdAt', direction: 'desc' },
  activeFilters: [],
  trendFilters: [],
  evidenceInclusion: 'total',
  suggested: true,
  aiAgentPanelVariant: 'default',
  validateHelperFullyAddressedIds: null,

  // Theme State
  selectedTheme: null,
  themeEvidenceIds: null,
  
  // Modal State
  verifyOpened: false,
  validateLoader: false,
  opened: false,
  
  // Perspective State
  perspectiveData: {},
  perspectiveDataLoading: false,
  chosenKey: 'trend',
  
  // Graph Selection State
  graphSelection: 'All',
  
  // Theme management defaults
  themeLoading: false,
  setThemeLoading: () => {
    console.log('setThemeLoading called with default implementation');
  },
  recalculateThemes: false,
  setRecalculateThemes: () => {
    console.log('setRecalculateThemes called with default implementation');
  },
  filteredThemeEvidenceIds: [],
  setFilteredThemeEvidenceIds: () => {
    console.log('setFilteredThemeEvidenceIds called with default implementation');
  },
  setIsThemeFound: () => {
    console.log('setIsThemeFound called with default implementation');
  },

};

const isDateFilter = (filter: any): boolean => {
  const name = (filter.name || '').toLowerCase();
  if (filter.type === 'date') return true;
  if (name === 'createdat' || name === 'created-at' || name === 'created_at') return true;
  if (Array.isArray(filter.selected) && filter.selected.length === 2) {
    const [start, end] = filter.selected;
    if (
      typeof start === 'string' && typeof end === 'string' &&
      start.match(/^\d{4}-\d{2}-\d{2}T/) && end.match(/^\d{4}-\d{2}-\d{2}T/)
    ) return true;
  }
  return false;
};

const IdeaPageContext = createContext<IdeaPageContextType | null>(null);

export const useIdeaPageContext = () => {
  const context = useContext(IdeaPageContext);
  if (!context) {
    throw new Error('useIdeaPageContext must be used within an IdeaPageProvider');
  }
  return context;
};

interface IdeaPageProviderProps {
  children: ReactNode;
  initialTab?: string;
}

export const IdeaPageProvider: React.FC<IdeaPageProviderProps> = ({ 
  children, 
  initialTab 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ideasFiltersConfig } = useIdeaFilters();
  
  // Parse URL parameters to get initial filters
  const { initialNonTimeFilters, initialTimeFilters } = React.useMemo(() => {
    console.log('🔍 [IdeaPageContext] useMemo running with location.search:', location.search);
    
    try {
      const parsedFilters = parseFiltersFromQuery(location.search, ideasFiltersConfig);
  
      // Filter to only include evidence filters
      const evidenceFilters = parsedFilters?.filter((filter: any) => filter.filterObject === 'evidence') || [];
      
      // Separate CreatedAt/date filters (for trendFilters) from other filters (for activeFilters)
      // Check name, type, and selected value format to catch all date filters
      const nonTimeFilters = evidenceFilters?.filter((filter: any) => !isDateFilter(filter)) || [];
      const timeFilters = evidenceFilters?.filter((filter: any) => isDateFilter(filter)) || [];
      
      console.log('🔍 [IdeaPageContext] Filter separation:', {
        evidenceFilters: evidenceFilters?.map((f: any) => ({ name: f.name, type: f.type, filterObject: f.filterObject })),
        nonTimeFilters: nonTimeFilters?.map((f: any) => ({ name: f.name, type: f.type })),
        timeFilters: timeFilters?.map((f: any) => ({ name: f.name, type: f.type }))
      });
   
      console.log('🔍 [IdeaPageContext] Parsed URL filters:', {
        locationSearch: location.search,
        parsedFiltersCount: parsedFilters?.length || 0,
        parsedFilters,
        evidenceFiltersCount: evidenceFilters?.length || 0,
        evidenceFilters,
        nonTimeFiltersCount: nonTimeFilters?.length || 0,
        timeFiltersCount: timeFilters?.length || 0,
        hasIdeasFiltersConfig: !!ideasFiltersConfig
      });

      return { initialNonTimeFilters: nonTimeFilters, initialTimeFilters: timeFilters };
    } catch (error) {
      console.warn('Error parsing URL filters:', error);
      return { initialNonTimeFilters: [], initialTimeFilters: [] };
    }
  }, [location.search, ideasFiltersConfig]);
  
  // UI State
  const [activeTab, setActiveTab] = useState<string | null>(initialTab || null);
  const [isGenerated, setIsGenerated] = useState<boolean>(false);
  const [isValidationDone, setIsValidationDone] = useState<boolean>(false);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<any[]>([]);
  const [chosenEvidence, setChosenEvidence] = useState<any | null>(null);
  const [addEvidenceFormScreen, setAddEvidenceFormScreen] = useState<boolean>(false);
  const [activityOpened, setActivityOpened] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isThemeFound, setIsThemeFound] = useState<boolean>(true);
  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ field?: string; direction?: 'asc' | 'desc' }>({ field: 'createdAt', direction: 'desc' });
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialNonTimeFilters);
  const [trendFilters, setTrendFilters] = useState<ActiveFilter[]>(initialTimeFilters);
  const [evidenceInclusion, setEvidenceInclusion] = useState<EvidenceInclusion>('total');
  const [aiAgentPanelVariant, setAiAgentPanelVariant] = useState<'default' | 'validate'>('default');
  const [validateHelperFullyAddressedIds, setValidateHelperFullyAddressedIds] = useState<string[] | null>(null);

  console.log('🔍 [IdeaPageContext] After useState - activeFilters:', activeFilters?.length, 'trendFilters:', trendFilters?.length, 'trendFilters content:', trendFilters);

  // Track if we've applied URL filters to avoid re-applying
  const hasAppliedUrlFiltersRef = React.useRef(false);
  // Track the last filter-param signature of the URL so we don't re-apply
  // URL filters when only non-filter params (e.g. evidenceId/requestId) change.
  // This prevents removed filter chips from reappearing after opening an evidence.
  const lastFilterParamsSignatureRef = React.useRef<string | null>(null);
  // Track the filter-field structure of the config we last parsed against so
  // we re-enrich when new filter fields arrive, but NOT on every React Query
  // background refresh (which would fire configChanged while the URL still has
  // stale filter params and undo the user's X-press before state→URL runs).
  const lastFiltersConfigStructureSignatureRef = React.useRef<string | null>(null);
  const filtersConfigStructureSignature = useMemo(
    () => buildFiltersConfigStructureSignature(ideasFiltersConfig),
    [ideasFiltersConfig]
  );
  // Set to true by the URL→state effect whenever it calls setActiveFilters so
  // that the state→URL effect in the same commit knows active-filter state is
  // stale and must not clobber the URL that Effect 1 just processed.
  const urlJustUpdatedFiltersRef = React.useRef(false);

  // Update activeFilters and trendFilters when initial filters change (e.g., when ideasFiltersConfig loads)
  // This ensures URL filters are applied even if they weren't parsed on initial render
  React.useEffect(() => {
    console.log('🔍 [IdeaPageContext] useEffect triggered:', {
      initialNonTimeFiltersLength: initialNonTimeFilters.length,
      initialTimeFiltersLength: initialTimeFilters.length,
      hasAppliedUrlFilters: hasAppliedUrlFiltersRef.current,
      initialNonTimeFilters,
      initialTimeFilters
    });
    
    const hasFilters = initialNonTimeFilters.length > 0 || initialTimeFilters.length > 0;
    if (hasFilters && !hasAppliedUrlFiltersRef.current) {
      console.log('🔍 [IdeaPageContext] Applying URL filters to activeFilters and trendFilters');
      hasAppliedUrlFiltersRef.current = true;
      setActiveFilters(initialNonTimeFilters);
      setTrendFilters(initialTimeFilters);
    }
  }, [initialNonTimeFilters, initialTimeFilters]);
  
  // Theme State
  const [selectedTheme, setSelectedTheme] = useState<any | null>(null);
  const [themeEvidenceIds, setThemeEvidenceIds] = useState<any[] | null>(null);
  
  // Modal State
  const [verifyOpened, setVerifyOpened] = useState<boolean>(false);
  const [validateLoader, setValidateLoader] = useState<boolean>(false);
  const [opened, setOpened] = useState<boolean>(false);
  
  // Perspective State
  const [perspectiveData, setPerspectiveData] = useState<{ [key: string]: any }>({});
  const [perspectiveDataLoading, setPerspectiveDataLoading] = useState<boolean>(false);
  const [chosenKey, setChosenKey] = useState<string>('trend');
  
  // Graph Selection State
  const [graphSelection, setGraphSelection] = useState<string>('All');
  
  // Theme management state
  const [themeLoading, setThemeLoading] = useState<boolean>(false);
  const [recalculateThemes, setRecalculateThemes] = useState<boolean>(false); 
  const [filteredThemeEvidenceIds, setFilteredThemeEvidenceIds] = useState<any[]>([]);
  const [unreadNotificationsMap, setUnreadNotificationsMap] = useState<any[]>(
    []
  );
  const [ideaHistoryView, setIdeaHistoryView] = useState<boolean>(false);
  
  // Update activeFilters when URL changes (for back/forward navigation)
  // But ONLY if filter-related params actually changed (not evidenceId/requestId)
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);

      // Build a stable signature of just the filter.* params so we can detect
      // whether they actually changed between URL updates. Without this, opening
      // an evidence (which only adds evidenceId/requestId) would re-trigger this
      // effect and re-apply the URL filters, causing manually-removed filter
      // chips to reappear.
      const filterParamEntries = Array.from(searchParams.entries())
        .filter(([key]) => key.startsWith('filter.'))
        .sort(([a], [b]) => a.localeCompare(b));
      const filterParamsSignature = JSON.stringify(filterParamEntries);
      const hasFilterParams = filterParamEntries.length > 0;

      const isFirstRun = lastFilterParamsSignatureRef.current === null;
      const filterParamsChanged =
        !isFirstRun && lastFilterParamsSignatureRef.current !== filterParamsSignature;
      // Re-parse when the filter-field structure of the config changes (e.g.
      // React Query resolves the real config with `type`, `dbFieldPath`,
      // `label`, etc.). Using a structure signature rather than reference
      // equality means background refetches that only update option values
      // (members, quarters, etc.) won't re-trigger this effect and undo a
      // filter the user just removed.
      const configChanged =
        filtersConfigStructureSignature !== '' &&
        lastFiltersConfigStructureSignatureRef.current !== filtersConfigStructureSignature;

      lastFilterParamsSignatureRef.current = filterParamsSignature;
      lastFiltersConfigStructureSignatureRef.current = filtersConfigStructureSignature;

      // Only update from URL when:
      //  - it's the first run with filter params (initial load), OR
      //  - the filter params actually changed (back/forward navigation, edits), OR
      //  - the filters config just loaded/changed (need to enrich filters).
      // This prevents resetting filters when only non-filter params (like
      // evidenceId/requestId) change, which would otherwise re-add filter
      // chips the user just removed.
      if (hasFilterParams && (isFirstRun || filterParamsChanged || configChanged)) {
        const parsedFilters = parseFiltersFromQuery(location.search, ideasFiltersConfig);
        const evidenceFilters = parsedFilters?.filter((filter: any) => filter.filterObject === 'evidence') || [];
        
        // Separate date filters from other filters (same logic as initial parse)
        const nonTimeFilters = evidenceFilters?.filter((filter: any) => !isDateFilter(filter)) || [];
        const timeFilters = evidenceFilters?.filter((filter: any) => isDateFilter(filter)) || [];
        
        console.log('🔍 [IdeaPageContext] URL change - filter separation:', {
          evidenceFilters: evidenceFilters?.map((f: any) => ({ name: f.name, type: f.type })),
          nonTimeFilters: nonTimeFilters?.map((f: any) => ({ name: f.name, type: f.type })),
          timeFilters: timeFilters?.map((f: any) => ({ name: f.name, type: f.type }))
        });
        
        urlJustUpdatedFiltersRef.current = true;
        setActiveFilters(nonTimeFilters);
        setTrendFilters(timeFilters);
      } else if (filterParamsChanged && !hasFilterParams) {
        // URL shed all filter.* params (e.g. back nav to a no-filter URL).
        // Without this branch Effect 1 skips the state update, Effect 2 then
        // sees stale activeFilters, detects a signature mismatch vs the empty
        // URL, and writes the old filters back — undoing the navigation.
        urlJustUpdatedFiltersRef.current = true;
        setActiveFilters([]);
        setTrendFilters([]);
      }
    } catch (error) {
      console.warn('Error updating filters from URL:', error);
    }
  }, [location.search, ideasFiltersConfig]);

  // Sync active/trend filter state back into the URL `filter.*` params so the
  // URL is the source of truth across refresh, share, and back/forward.
  // Without this, removing a filter chip on the idea page only updates React
  // state and a refresh re-applies the stale URL filters.
  // Skip the very first render so we don't race with the initial URL→state
  // parse (especially while `ideasFiltersConfig` is still loading).
  const isFirstUrlSyncRef = React.useRef(true);
  useEffect(() => {
    if (isFirstUrlSyncRef.current) {
      isFirstUrlSyncRef.current = false;
      return;
    }

    // If Effect 1 (URL→state) just queued a setActiveFilters in this same
    // commit, activeFilters is stale here. Bail out so we don't navigate to
    // stale filter state and clobber the URL; the next render (after state
    // settles) will reconcile correctly.
    if (urlJustUpdatedFiltersRef.current) {
      urlJustUpdatedFiltersRef.current = false;
      return;
    }

    try {
      const newFilterParams = buildFilterUrlParams([
        ...(activeFilters || []),
        ...(trendFilters || []),
      ]);

      const currentSearchParams = new URLSearchParams(location.search);

      // Build a sorted snapshot of current filter.* params for diffing.
      const currentFilterEntries = Array.from(currentSearchParams.entries())
        .filter(([key]) => key.startsWith('filter.'))
        .sort(([a], [b]) => a.localeCompare(b));
      const currentFilterSignature = JSON.stringify(currentFilterEntries);

      const nextFilterEntries = Object.entries(newFilterParams)
        .sort(([a], [b]) => a.localeCompare(b));
      const nextFilterSignature = JSON.stringify(nextFilterEntries);

      if (currentFilterSignature === nextFilterSignature) return;

      // Strip stale filter.* keys, then write the new ones, preserving any
      // unrelated query params (evidenceId, requestId, selectedTheme, etc.).
      Array.from(currentSearchParams.keys())
        .filter((key) => key.startsWith('filter.'))
        .forEach((key) => currentSearchParams.delete(key));
      nextFilterEntries.forEach(([key, value]) => {
        currentSearchParams.set(key, value);
      });

      // Pre-update the signature ref so the URL→state effect that fires from
      // this navigate() call recognises it as a self-write and skips re-applying.
      lastFilterParamsSignatureRef.current = nextFilterSignature;

      const nextSearch = currentSearchParams.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      );
    } catch (error) {
      console.warn('Error syncing filters to URL:', error);
    }
    // `location.search` is included so that non-filter URL changes (e.g. an
    // evidenceId being appended) which land in the same render batch as a
    // filter change don't get clobbered by a stale closure on `navigate()`.
  }, [activeFilters, trendFilters, location.search]);

  const state = useMemo(() => evidenceInclusionToState(evidenceInclusion), [evidenceInclusion]);

  const setSuggested = (checked: boolean) => {
    const currentAssigned = state.assigned;
    if (!checked && !currentAssigned) {
      return;
    }
    const newEvidenceInclusion = stateToEvidenceInclusion(currentAssigned, checked);
    setEvidenceInclusion(newEvidenceInclusion);
  };
  
  // Actions
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setIsSearching(false);
  }, []);
  
  const clearSort = useCallback(() => {
    setSortConfig({ field: 'createdAt', direction: 'desc' });
  }, []);
  
  const contextValue: IdeaPageContextType = {
    // State
    activeTab,
    suggested: state.suggested,
    evidenceInclusion,
    isGenerated,
    isValidationDone,
    selectedEvidenceIds,
    chosenEvidence,
    addEvidenceFormScreen,
    activityOpened,
    isSearching,
    isThemeFound,
    ideaHistoryView,
    setIdeaHistoryView,
    searchQuery,
    debouncedSearchQuery,
    sortConfig,
    activeFilters,
    trendFilters,
    aiAgentPanelVariant,
    validateHelperFullyAddressedIds,
    selectedTheme,
    themeEvidenceIds,
    filteredThemeEvidenceIds,
    setFilteredThemeEvidenceIds,
    verifyOpened,
    validateLoader,
    opened,
    perspectiveData,
    perspectiveDataLoading,
    chosenKey,
    graphSelection,
    unreadNotificationsMap, 
    setUnreadNotificationsMap,
    
    // Theme management
    themeLoading,
    setThemeLoading,
    recalculateThemes,
    
    // Actions
    setActiveTab,
    setIsGenerated,
    setIsValidationDone,
    setSelectedEvidenceIds,
    setChosenEvidence,
    setAddEvidenceFormScreen,
    setActivityOpened,
    setIsSearching,
    setIsThemeFound,
    setSearchQuery,
    setDebouncedSearchQuery,
    setSortConfig,
    setActiveFilters,
    setTrendFilters,
    setEvidenceInclusion,
    clearSearch,
    clearSort,
    setSelectedTheme,
    setThemeEvidenceIds,
    setVerifyOpened,
    setValidateLoader,
    setOpened,
    setPerspectiveData,
    setPerspectiveDataLoading,
    setChosenKey,
    setGraphSelection,
    setRecalculateThemes,
    setSuggested,
    setAiAgentPanelVariant,
    setValidateHelperFullyAddressedIds
  };    
  
  return (
    <IdeaPageContext.Provider value={contextValue}>
      <IdeaPagePerspectiveFetcher />
      {children}
    </IdeaPageContext.Provider>
  );
};

export default IdeaPageContext;
